import { useState } from 'react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Button, TextArea, TextField } from '@/components/ds2'
import { PanelDrawer } from './PanelDrawer'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { showError, showSuccess } from '@/utils/snackbar'
import { useActiveCompany } from '@/features/organization/hooks/useActiveCompany'
import { useInvoicingRowsStore } from '../../invoicing/rowsStore'
import { useInvoicingChat } from '../../invoicing/chatBridge'
import { useInvoiceEmailSender } from '../../hooks/useInvoiceEmailSender'
import { formatUsd } from '../results/format'
import { amountOf, emailBlockOf, rowKeyOf } from './invoicingRows'
import { sendInvoiceEmails, type EmailItem, type EmailOutcome } from './sendBatch'
import { useBillingEmails } from './useBillingEmails'
import { outcomeOfMany, trackedInvoicingAction } from '../../invoicing/invoicingAnalytics'

interface InvoiceEmailDrawerProps {
    open: boolean
    /** The selected loads, in table order. A blocked one stays listed with its reason and a Remove link. */
    rows: TmsInvoiceRow[]
    onClose: () => void
    onRemove: (key: string) => void
    /** Runs after every email has been tried, with each load's outcome. */
    onDone: (outcomes: EmailOutcome[]) => void
}

interface Draft {
    to: string
    /** The Cc as typed: addresses separated by commas. */
    cc: string
    subject: string
    message: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The subject and message a row starts with (v17): they name the load, its total and the company sending it (the one the user acts as,
 * as the sidebar shows it), and the send takes them as the user leaves them. With no company name known (only the project's id), the
 * name is left out rather than signing with an id.
 */
const defaultDraft = (row: TmsInvoiceRow, companyName: string | null, cc: string[], to: string): Draft => {
    const number = row.load_numbers[0] ?? ''
    return {
        to,
        cc: cc.join(', '),
        subject: companyName ? `Invoice for load #${number} · ${companyName}` : `Invoice for load #${number}`,
        message: `Hello,\n\nPlease find the invoice and supporting documents for load #${number}, totaling ${formatUsd(amountOf(row))}.\n\nThank you,${companyName ? `\n${companyName}` : ''}`,
    }
}

/**
 * What the email carries: one PDF, the invoice with the load's documents on file merged after it (the invoice PDF's packet). Said
 * from the row's documents, so a load without a rate con or POD does not promise them.
 */
const attachmentLineOf = (row: TmsInvoiceRow): string => {
    const docs = row.documents
    if (!docs) return 'Attachment: the invoice PDF with the load documents on file'
    const included = [docs.rate_con ? 'rate confirmation' : null, docs.bol ? 'BOL' : null, docs.pod !== 'none' ? 'POD' : null].filter(Boolean)
    return included.length > 0
        ? `Attachment: the invoice PDF with the ${included.join(', ')}`
        : 'Attachment: the invoice PDF only (no rate con, BOL or POD on file)'
}

/** The Cc field's addresses, blanks dropped. */
const ccListOf = (cc: string): string[] =>
    cc
        .split(',')
        .map((address) => address.trim())
        .filter(Boolean)

const isValid = (draft: { to?: string; cc?: string[]; subject: string; message: string }): boolean => {
    // A recipient is always shown and sent as shown: the send never picks one the user did not see.
    if (!draft.to?.trim() || !EMAIL_PATTERN.test(draft.to.trim())) return false
    if (draft.cc?.some((address) => !EMAIL_PATTERN.test(address))) return false
    return Boolean(draft.subject.trim() && draft.message.trim())
}

/** Re-reads the rows and the thread after a send. A failed read is shown; it never undoes what was sent. */
const refreshAfterSend = async (refreshThread: () => Promise<void>, tabId: string, rows: TmsInvoiceRow[]): Promise<void> => {
    try {
        await useInvoicingRowsStore.getState().refresh(rows.map((row) => row.load_numbers[0]).filter((n): n is string => Boolean(n)))
        await refreshThread()
    } catch (failure) {
        showError(getUserErrorMessage(failure))
    }
}

/**
 * Review invoice emails (v17 flow 5): one block per selected load with its recipient, subject and message, and the button that sends
 * them all. The button is the approval for the sends (tier 1); each invoice is created first when it has none.
 */
export const InvoiceEmailDrawer = ({ open, rows, onClose, onRemove, onDone }: InvoiceEmailDrawerProps) => {
    const [drafts, setDrafts] = useState<Record<string, Draft>>({})
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const chat = useInvoicingChat()
    const { companyName, hasRealCompanyName } = useActiveCompany()
    // The sender and the Cc the Invoices page's email dialog opens with (the same shared rule), shown so nothing goes out unseen.
    const { from, defaultCc } = useInvoiceEmailSender()
    // Each email's To, read before the click with the send's own rule, so the user approves the address they see.
    const billing = useBillingEmails(rows, open)

    if (!open) return null

    const sender = hasRealCompanyName ? companyName : null
    const blocked = rows.filter((row) => emailBlockOf(row) !== null)
    const eligible = rows.filter((row) => emailBlockOf(row) === null)
    const draftOf = (row: TmsInvoiceRow): Draft => drafts[rowKeyOf(row)] ?? defaultDraft(row, sender, defaultCc, billing[rowKeyOf(row)] ?? '')
    const reading = eligible.some((row) => billing[rowKeyOf(row)] === undefined && !drafts[rowKeyOf(row)])
    const patch = (row: TmsInvoiceRow, change: Partial<Draft>) => setDrafts((current) => ({ ...current, [rowKeyOf(row)]: { ...draftOf(row), ...change } }))

    const send = async () => {
        const items: EmailItem[] = eligible.map((row) => {
            const draft = draftOf(row)
            return { row, to: draft.to, cc: ccListOf(draft.cc), subject: draft.subject, message: draft.message }
        })
        if (!items.every((item) => isValid(item))) {
            setError('Enter a recipient, a valid Cc, a subject and a message for every invoice.')
            return
        }
        setError('')
        setBusy(true)
        try {
            const tabId = await chat.ensureTab()
            const outcomes = await trackedInvoicingAction(
                'send_email',
                () => sendInvoiceEmails(tabId, items),
                (sent) => outcomeOfMany(sent.filter((outcome) => outcome.sent).length, sent.length),
                { count: items.length }
            )
            // The emails are out: report them before any read that can fail, so a failed refresh never hides what was sent.
            const failed = outcomes.filter((outcome) => !outcome.sent)
            if (failed.length === 0) showSuccess('Invoice emails sent.')
            else showError(`${failed.length} of ${outcomes.length} emails were not sent. ${failed[0].message}`)
            onDone(outcomes)
            await refreshAfterSend(chat.refreshThread, tabId, eligible)
        } catch (failure) {
            showError(getUserErrorMessage(failure))
        } finally {
            setBusy(false)
        }
    }

    return (
        <PanelDrawer
            open={open}
            onClose={onClose}
            title="Review invoice emails"
            subtitle={<>Factoring not connected · Send directly to customers</>}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={() => void send()} disabled={blocked.length > 0 || eligible.length === 0 || busy || !from || reading}>
                        {busy ? 'Sending…' : `Send ${eligible.length} ${eligible.length === 1 ? 'email' : 'emails'}`}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4 text-sm">
                <p className="text-xs text-foreground-muted">
                    Each email goes to the accounting email on its load's rate con, as on the Invoices page. Check the recipient before sending.
                </p>
                {from ? (
                    <p className="rounded-lg bg-surface-secondary px-3 py-2 text-xs text-foreground-muted">
                        Sent from <strong className="font-medium text-foreground">{from}</strong> (your Invoice Settings).
                    </p>
                ) : (
                    <p role="alert" className="rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
                        No mailbox is connected to send from. Connect one in Settings → Email first.
                    </p>
                )}
                {rows.map((row) => {
                    const block = emailBlockOf(row)
                    const number = row.load_numbers[0] ?? ''
                    const draft = draftOf(row)
                    return (
                        <section key={rowKeyOf(row)} className="flex flex-col gap-3 rounded-xl border border-border p-3">
                            <div className="flex items-center justify-between gap-2">
                                <strong className="text-foreground">
                                    {row.broker ?? '—'} · #{number}
                                </strong>
                                <span className="tabular-nums text-foreground-muted">{formatUsd(amountOf(row))}</span>
                            </div>
                            {block ? (
                                <div className="flex items-center justify-between gap-2 rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
                                    <span>{block}</span>
                                    <button type="button" className="underline" onClick={() => onRemove(rowKeyOf(row))}>
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <TextField
                                        label="To · Customer billing email"
                                        inputType="email"
                                        placeholder={
                                            billing[rowKeyOf(row)] === undefined
                                                ? 'Reading the rate con’s billing email…'
                                                : 'No billing email on the rate con: type one'
                                        }
                                        value={draft.to}
                                        onChange={(event) => patch(row, { to: event.target.value })}
                                        showDescription={false}
                                    />
                                    <TextField
                                        label="Cc"
                                        placeholder="No Cc"
                                        value={draft.cc}
                                        onChange={(event) => patch(row, { cc: event.target.value })}
                                        showDescription={false}
                                    />
                                    <TextField
                                        label="Subject"
                                        value={draft.subject}
                                        onChange={(event) => patch(row, { subject: event.target.value })}
                                        showDescription={false}
                                    />
                                    <TextArea
                                        label="Message"
                                        rows={4}
                                        value={draft.message}
                                        onChange={(event) => patch(row, { message: event.target.value })}
                                        showDescription={false}
                                    />
                                    <small className="text-xs text-foreground-muted">{attachmentLineOf(row)}</small>
                                </>
                            )}
                        </section>
                    )
                })}
                {error && (
                    <p role="alert" className="text-xs text-danger">
                        {error}
                    </p>
                )}
            </div>
        </PanelDrawer>
    )
}
